/* import __COLOCATED_TEMPLATE__ from './refund-order-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';

import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import type { ShopifyOrder } from '../shopify';
import { taskFor } from 'ember-concurrency-ts';

interface Signature {
  Args: {
    onModalClose: () => void;
    isOpen: boolean;
    order: ShopifyOrder;
    shopifyDomain: string | null;
    onRefundSuccess?: () => void;
  };
}

interface SelectedLineItem {
  lineItemId: number;
  quantity: number;
  restock: boolean;
}

interface LineItemReason {
  lineItemId: number;
  reason: string;
}

export default class RefundOrderModal extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;

  @tracked note = '';
  @tracked restockItem = true;
  @tracked notifyCustomer = false;
  @tracked selectedLineItems: SelectedLineItem[] = [];
  @tracked refundShipping = true;
  @tracked lineItemReasons: LineItemReason[] = [];
  @tracked shippingRefundReason = '';
  @tracked genericRefundReason = '';
  @tracked isRefundAllChecked = false;

  get isPartialRefundEnabled() {
    return this.session.workspace.isFeatureEnabled('tdf-shopify-partial-refunds');
  }

  get isPerItemRefundReasonEnabled() {
    return (
      this.isPartialRefundEnabled &&
      this.session.workspace.isFeatureEnabled('tdf-shopify-per-item-refund-reason')
    );
  }

  get isPerOrderRefundReasonDropdownEnabled() {
    return (
      !this.isPerItemRefundReasonEnabled &&
      this.session.workspace.isFeatureEnabled('tdf-shopify-per-order-refund-reason-dropdown')
    );
  }

  get areAllItemsFullySelected(): boolean {
    return this.args.order.line_items.every((item) => {
      if (!this.isLineItemRefundable(item)) {
        return true;
      }
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === item.id);
      if (!selection) {
        return false;
      }
      let maxQty = item.quantity_refundable ?? item.quantity;
      return selection.quantity === maxQty;
    });
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    if (this.isPartialRefundEnabled) {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
          restock: this.restockItem,
        }));

      let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
      let shippingAmount = parseFloat((this.args.order.shipping || '0').replace(/[^0-9.]/g, ''));
      this.refundShipping = shippingAmount > 0 && !shippingAlreadyRefunded;
    }
  }

  get currencySymbol() {
    return this.args.order.total?.charAt(0) ?? '$';
  }

  get refundAmount() {
    return this.args.order.total?.substring(1) ?? '0.00';
  }

  get hasShippingToRefund() {
    if (!this.isPartialRefundEnabled) {
      return false;
    }
    let shippingAmount = parseFloat((this.args.order.shipping || '0').replace(/[^0-9.]/g, ''));
    let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
    return shippingAmount > 0 && !shippingAlreadyRefunded;
  }

  get calculatedRefundAmount() {
    if (!this.isPartialRefundEnabled) {
      return this.refundAmount;
    }

    void this.refundShipping;

    let subtotal = 0;
    for (let selection of this.selectedLineItems) {
      let lineItem = this.args.order.line_items.find((li) => li.id === selection.lineItemId);
      if (lineItem) {
        let unitPrice = parseFloat(lineItem.price.replace(/[^0-9.]/g, ''));
        let totalDiscount = lineItem.total_discount
          ? parseFloat(lineItem.total_discount.replace(/[^0-9.]/g, ''))
          : 0;
        let discountPerUnit = totalDiscount / lineItem.quantity;
        let discountedUnitPrice = unitPrice - discountPerUnit;
        subtotal += discountedUnitPrice * selection.quantity;
      }
    }

    let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
    let shippingAmount =
      this.refundShipping && !shippingAlreadyRefunded
        ? parseFloat((this.args.order.shipping || '0').replace(/[^0-9.]/g, ''))
        : 0;

    let orderSubtotal = parseFloat((this.args.order.subtotal || '0').replace(/[^0-9.]/g, ''));
    let orderTax = parseFloat((this.args.order.tax || '0').replace(/[^0-9.]/g, ''));
    let proportionalTax = orderSubtotal > 0 ? (subtotal / orderSubtotal) * orderTax : 0;

    let total = subtotal + shippingAmount + proportionalTax;
    return total.toFixed(2);
  }

  get isFullRefund(): boolean {
    if (!this.isPartialRefundEnabled) {
      return true;
    }

    void this.refundShipping;

    let allItemsSelected = this.args.order.line_items.every((item) => {
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === item.id);
      if (!selection || selection.quantity === 0) {
        return false;
      }
      let maxQty = item.quantity_refundable ?? item.quantity;
      return selection.quantity === maxQty;
    });

    return allItemsSelected && this.refundShipping;
  }

  get hasSelectedItems() {
    return this.selectedLineItems.length > 0;
  }

  get hasRefundableAmount() {
    let amount = parseFloat(this.calculatedRefundAmount);
    return amount > 0;
  }

  get lineItemRefundReasons() {
    return [
      {
        value: 'wrong_size',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.wrong_size',
        ),
      },
      {
        value: 'wrong_item',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.wrong_item',
        ),
      },
      {
        value: 'damaged',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.damaged',
        ),
      },
      {
        value: 'not_as_described',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.not_as_described',
        ),
      },
      {
        value: 'no_longer_wanted',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.no_longer_wanted',
        ),
      },
      {
        value: 'changed_mind',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.changed_mind',
        ),
      },
      {
        value: 'arrived_late',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.arrived_late',
        ),
      },
    ];
  }

  get shippingRefundReasons() {
    return [
      {
        value: 'wrong_address',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.wrong_address',
        ),
      },
      {
        value: 'delayed_delivery',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.delayed_delivery',
        ),
      },
      {
        value: 'damaged_in_transit',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.damaged_in_transit',
        ),
      },
      {
        value: 'customer_request',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.customer_request',
        ),
      },
      {
        value: 'shipping_cost_error',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.shipping_cost_error',
        ),
      },
    ];
  }

  get genericRefundReasons() {
    return [
      {
        value: 'customer_request',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.customer_request',
        ),
      },
      {
        value: 'defective_product',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.defective_product',
        ),
      },
      {
        value: 'wrong_item_sent',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.wrong_item_sent',
        ),
      },
      {
        value: 'customer_changed_mind',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.customer_changed_mind',
        ),
      },
      {
        value: 'order_error',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.order_error',
        ),
      },
      {
        value: 'delivery_issue',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.delivery_issue',
        ),
      },
      {
        value: 'quality_issue',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.quality_issue',
        ),
      },
    ];
  }

  get hasAllRequiredReasons(): boolean {
    if (this.isPartialRefundEnabled && this.isRefundAllChecked) {
      return Boolean(this.genericRefundReason);
    }

    if (this.isPerOrderRefundReasonDropdownEnabled) {
      return Boolean(this.genericRefundReason);
    }

    if (!this.isPerItemRefundReasonEnabled) {
      return true;
    }

    let refundingItems = this.selectedLineItems.filter((sel) => sel.quantity > 0);
    let allItemsHaveReasons = refundingItems.every((sel) =>
      this.lineItemReasons.some((r) => r.lineItemId === sel.lineItemId && r.reason),
    );

    let shippingReasonValid =
      !this.refundShipping || !this.hasShippingToRefund || Boolean(this.shippingRefundReason);

    return allItemsHaveReasons && shippingReasonValid;
  }

  get getLineItemReason() {
    void this.lineItemReasons.length;

    return (lineItemId: number): string => {
      return this.lineItemReasons.find((r) => r.lineItemId === lineItemId)?.reason ?? '';
    };
  }

  get isLineItemSelected() {
    void this.selectedLineItems.length;

    return (lineItemId: number): boolean => {
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
      return selection ? selection.quantity > 0 : false;
    };
  }

  get getSelectedQuantity() {
    void this.selectedLineItems.length;

    return (lineItemId: number): number => {
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
      return selection?.quantity ?? 0;
    };
  }

  get getLineItemTotal() {
    void this.selectedLineItems.length;

    return (lineItemId: number, lineItem: any): string => {
      if (!this.isPartialRefundEnabled) {
        return lineItem.tertiary_text;
      }

      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
      let quantity = selection?.quantity ?? 0;

      let unitPrice = parseFloat(lineItem.price.replace(/[^0-9.]/g, ''));
      let totalDiscount = lineItem.total_discount
        ? parseFloat(lineItem.total_discount.replace(/[^0-9.]/g, ''))
        : 0;
      let discountPerUnit = totalDiscount / lineItem.quantity;
      let discountedUnitPrice = unitPrice - discountPerUnit;
      let total = discountedUnitPrice * quantity;
      return this.currencySymbol + total.toFixed(2);
    };
  }

  get isLineItemRefundable() {
    return (lineItem: any): boolean => {
      if (!this.isPartialRefundEnabled) {
        return true;
      }
      let quantityRefundable = lineItem.quantity_refundable ?? lineItem.quantity;
      return quantityRefundable > 0;
    };
  }

  totalRefundQuantity(quantity: number) {
    return `${quantity} / ${quantity}`;
  }

  get isRefundOrderTaskRunning() {
    return taskFor(this.refundOrderTask).isRunning;
  }

  @action
  toggleRefundAll() {
    this.isRefundAllChecked = !this.isRefundAllChecked;
    if (this.isRefundAllChecked) {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id && this.isLineItemRefundable(item))
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
          restock: this.restockItem,
        }));
      if (this.hasShippingToRefund) {
        this.refundShipping = true;
      }
      this.lineItemReasons = [];
    } else {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({
          lineItemId: item.id,
          quantity: 0,
          restock: this.restockItem,
        }));
      this.refundShipping = false;
    }
  }

  @action
  toggleRefundShipping(value: boolean) {
    this.refundShipping = value;
    if (!value) {
      this.shippingRefundReason = '';
    }
  }

  @action
  updateLineItemReason(lineItemId: number, event: Event) {
    let target = event.target as HTMLSelectElement;
    let reason = target.value;
    let existingIndex = this.lineItemReasons.findIndex((r) => r.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      this.lineItemReasons = this.lineItemReasons.map((r) =>
        r.lineItemId === lineItemId ? { ...r, reason } : r,
      );
    } else {
      this.lineItemReasons = [...this.lineItemReasons, { lineItemId, reason }];
    }
  }

  @action
  updateShippingRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.shippingRefundReason = target.value;
  }

  @action
  updateGenericRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.genericRefundReason = target.value;
  }

  private updateLineItemQuantityValue(lineItemId: number, quantity: number) {
    let lineItem = this.args.order.line_items.find((li) => li.id === lineItemId);
    if (!lineItem) {
      return;
    }

    let maxQuantity = lineItem.quantity_refundable ?? lineItem.quantity;
    let validQuantity = isNaN(quantity) ? 0 : Math.min(Math.max(0, quantity), maxQuantity);

    let currentItems = this.selectedLineItems;
    let existingIndex = currentItems.findIndex((sel) => sel.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      let newItems = currentItems.map((sel) =>
        sel.lineItemId === lineItemId ? { ...sel, quantity: validQuantity } : sel,
      );
      this.selectedLineItems = newItems;
    } else {
      this.selectedLineItems = [
        ...currentItems,
        {
          lineItemId,
          quantity: validQuantity,
          restock: this.restockItem,
        },
      ];
    }

    this.isRefundAllChecked = false;
  }

  @action
  toggleLineItemSelection(lineItemId: number) {
    let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
    let isCurrentlySelected = selection ? selection.quantity > 0 : false;

    let lineItem = this.args.order.line_items.find((li) => li.id === lineItemId);
    if (!lineItem) {
      return;
    }

    let targetQuantity = isCurrentlySelected
      ? 0
      : (lineItem.quantity_refundable ?? lineItem.quantity);

    this.updateLineItemQuantityValue(lineItemId, targetQuantity);
    this.isRefundAllChecked = false;
  }

  @action
  updateLineItemQuantity(lineItemId: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let quantity = parseInt(target.value, 10);

    this.updateLineItemQuantityValue(lineItemId, quantity);
  }

  @action
  resetModalData() {
    this.note = '';
    this.restockItem = true;
    this.notifyCustomer = false;
    this.lineItemReasons = [];
    this.shippingRefundReason = '';
    this.genericRefundReason = '';
    this.isRefundAllChecked = false;

    let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
    let shippingAmount = parseFloat((this.args.order.shipping || '0').replace(/[^0-9.]/g, ''));
    this.refundShipping =
      this.isPartialRefundEnabled && shippingAmount > 0 && !shippingAlreadyRefunded;

    if (this.isPartialRefundEnabled) {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
          restock: this.restockItem,
        }));
    }
  }

  @action
  handleModalClose() {
    this.resetModalData();
    this.args.onModalClose();
  }

  private getLineItemReasonLabel(reasonValue: string): string {
    switch (reasonValue) {
      case 'wrong_size':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.wrong_size',
        );
      case 'wrong_item':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.wrong_item',
        );
      case 'damaged':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.damaged',
        );
      case 'not_as_described':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.not_as_described',
        );
      case 'no_longer_wanted':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.no_longer_wanted',
        );
      case 'changed_mind':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.changed_mind',
        );
      case 'arrived_late':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.line-item-reasons.arrived_late',
        );
      default:
        return '';
    }
  }

  private getShippingReasonLabel(reasonValue: string): string {
    switch (reasonValue) {
      case 'wrong_address':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.wrong_address',
        );
      case 'delayed_delivery':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.delayed_delivery',
        );
      case 'damaged_in_transit':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.damaged_in_transit',
        );
      case 'customer_request':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.customer_request',
        );
      case 'shipping_cost_error':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-reasons.shipping_cost_error',
        );
      default:
        return '';
    }
  }

  private getGenericReasonLabel(reasonValue: string): string {
    switch (reasonValue) {
      case 'customer_request':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.customer_request',
        );
      case 'defective_product':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.defective_product',
        );
      case 'wrong_item_sent':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.wrong_item_sent',
        );
      case 'customer_changed_mind':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.customer_changed_mind',
        );
      case 'order_error':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.order_error',
        );
      case 'delivery_issue':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.delivery_issue',
        );
      case 'quality_issue':
        return this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.generic-reasons.quality_issue',
        );
      default:
        return '';
    }
  }

  private buildRefundNote(): string {
    if (this.isPartialRefundEnabled && this.isRefundAllChecked && this.genericRefundReason) {
      return this.getGenericReasonLabel(this.genericRefundReason);
    }

    if (!this.isPerItemRefundReasonEnabled) {
      if (this.isPerOrderRefundReasonDropdownEnabled && this.genericRefundReason) {
        return this.getGenericReasonLabel(this.genericRefundReason);
      }
      return this.note;
    }

    let noteContent = '';

    let itemsWithReasons = this.selectedLineItems
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        let lineItem = this.args.order.line_items.find((li) => li.id === sel.lineItemId);
        let reason = this.lineItemReasons.find((r) => r.lineItemId === sel.lineItemId);
        if (!reason?.reason) {
          return '';
        }
        let reasonLabel = this.getLineItemReasonLabel(reason.reason);
        return `${lineItem?.title} (x${sel.quantity}): ${reasonLabel}`;
      })
      .filter((item) => item !== '');

    if (itemsWithReasons.length > 0) {
      noteContent = itemsWithReasons.join(' | ');
    }

    if (this.refundShipping && this.shippingRefundReason) {
      let shippingReasonLabel = this.getShippingReasonLabel(this.shippingRefundReason);
      if (shippingReasonLabel) {
        let shippingLabel = this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-label',
        );
        if (noteContent) {
          noteContent = `${noteContent} | ${shippingLabel}: ${shippingReasonLabel}`;
        } else {
          noteContent = `${shippingLabel}: ${shippingReasonLabel}`;
        }
      }
    }

    return noteContent.trim();
  }

  @restartableTask
  *refundOrderTask(): TaskGenerator<void> {
    try {
      let params: any = {
        app_id: this.session.workspace.id,
        order_id: this.args.order.id,
        shopify_domain: this.args.shopifyDomain,
        note: this.buildRefundNote(),
        notify_customer: this.notifyCustomer,
      };

      if (this.isPartialRefundEnabled && !this.isFullRefund) {
        params.refund_type = 'partial';
        params.line_items_to_refund = this.selectedLineItems
          .filter((selection) => selection.quantity > 0)
          .map((selection) => ({
            line_item_id: selection.lineItemId,
            quantity: selection.quantity,
            restock_item: selection.restock,
          }));
        let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
        params.refund_shipping = this.refundShipping && !shippingAlreadyRefunded;
      } else {
        params.refund_type = 'full';
        params.restock_item = this.restockItem;
      }

      yield ajax({
        url: '/ember/inbox/shopify/refund_order',
        type: 'POST',
        data: JSON.stringify(params),
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.success-message',
        ),
      );

      this.handleModalClose();

      if (this.args.onRefundSuccess) {
        this.args.onRefundSuccess();
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: 'refund_order',
        object: 'shopify_order_action',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        refund_type: this.isFullRefund ? 'full' : 'partial',
        items_count: this.selectedLineItems.length || this.args.order.line_items.length,
        partial_refunds_enabled: this.isPartialRefundEnabled,
      });
    } catch (error) {
      captureException(error);
      let response = error?.jqXHR?.responseJSON?.error;

      if (response?.message) {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t(
            'inbox.conversation-details-sidebar.shopify.refund-order-modal.detailed-error-message',
            { message: response.message },
          ),
        });
      } else {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t(
            'inbox.conversation-details-sidebar.shopify.refund-order-modal.error-message',
          ),
        });
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::RefundOrderModal': typeof RefundOrderModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/refund-order-modal': typeof RefundOrderModal;
  }
}
